import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from './hooks/useAuth';
import { useTenantStore } from './store/tenantStore';
import LoginForm from './components/LoginForm';
import PublicHome from './pages/PublicHome';
import SelectEmpresa from './pages/SelectEmpresa';
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout';
import EmpresasPage from './pages/superadmin/EmpresasPage';
import PlanesPage from './pages/superadmin/PlanesPage';
import SuscripcionesPage from './pages/superadmin/SuscripcionesPage';
import EmpresaUsuariosPage from './pages/superadmin/EmpresaUsuariosPage';
import Navigation from './components/Navigation';
import Dashboard from './pages/Dashboard';
import Pisos from './pages/Pisos';
import Areas from './pages/Areas';
import AreaTareasPage from './pages/AreaTareas';
import TipoAreas from './pages/TipoAreas';
import Tareas from './pages/Tareas';
import Registros from './pages/Registros';
import ErrorBoundary from './components/ErrorBoundary';
import QRPage from './pages/QRPage';
import QRLinks from './pages/QRLinks';
import Usuarios from './pages/Usuarios';
import ReportesPage from './modules/reportes/pages/ReportesPage';
import WhatsAppConfig from './pages/WhatsAppConfig';
import Solicitudes from './pages/Solicitudes';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Logout from './pages/Logout';
import ForgotPassword from './pages/ForgotPassword';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoadingUser, currentUser } = useAuth();
  const { tenantId } = useTenantStore();

  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // Si está autenticado pero sin tenant seleccionado, enviar a selector
  const pathname = window.location.pathname;
  const isSelectEmpresaRoute = pathname === '/select-empresa';
  const isSuperAdminRoute = pathname.startsWith('/super-admin');
  const isSuper = currentUser?.rol === 'SuperAdmin';
  if (!tenantId && !isSelectEmpresaRoute && !(isSuper && isSuperAdminRoute)) {
    return <Navigate to="/select-empresa" replace />;
  }
  return <>{children}</>;
};

const MainLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <main>
        <Outlet />
      </main>
    </div>
  );
};

const App: React.FC = () => {
  const router = createBrowserRouter(
    [
      { path: '/', element: <PublicHome /> },
      { path: '/login', element: <LoginForm /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/logout', element: <Logout /> },
      { path: '/signup', element: <PublicHome /> },
      { path: '/privacy', element: <Privacy /> },
      { path: '/terms', element: <Terms /> },
      {
        path: '/super-admin',
        element: (
          <ProtectedRoute>
            <SuperAdminLayout />
          </ProtectedRoute>
        ),
                 children: [
           { index: true, element: <EmpresasPage /> },
           { path: 'empresas', element: <EmpresasPage /> },
           { path: 'empresas/:empresaId/usuarios', element: <EmpresaUsuariosPage /> },
           { path: 'planes', element: <PlanesPage /> },
           { path: 'suscripciones', element: <SuscripcionesPage /> },
         ],
      },
      { path: '/select-empresa', element: (
        <ProtectedRoute>
          <SelectEmpresa />
        </ProtectedRoute>
      ) },
      { path: '/qr', element: <QRPage /> },
      {
        element: (
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: '/app', element: <Dashboard /> },
          { path: '/pisos', element: <Pisos /> },
          { path: '/areas', element: <Areas /> },
          { path: '/solicitudes', element: <Solicitudes /> },
          { path: '/reportes', element: <ReportesPage /> },
          { path: '/qr-links', element: <QRLinks /> },
          { path: '/usuarios', element: <Usuarios /> },
          { path: '/areas/:areaId/tareas', element: <AreaTareasPage /> },
          { path: '/tipo-areas', element: <TipoAreas /> },
          { path: '/tareas', element: <Tareas /> },
          { path: '/registros', element: <Registros /> },
          { path: '/whatsapp-config', element: <WhatsAppConfig /> },
        ],
      },
    ]
  );
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
